// Google Maps hook for centralized loading
import { useState, useEffect, useCallback } from 'react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SCRIPT_ID = 'google-maps-script';

// Global state to track loading
let isLoading = false;
let isLoaded = false;
let loadCallbacks = [];

// Check if Google Maps is fully ready
const isGoogleMapsReady = () => {
  return !!(window.google?.maps?.Map);
};

// Load Google Maps script once
const loadGoogleMapsScript = (callback) => {
  // If already loaded and Maps is ready
  if (isLoaded && isGoogleMapsReady()) {
    callback(null);
    return;
  }

  // Add callback to queue
  loadCallbacks.push(callback);

  // If already loading, wait for it
  if (isLoading) {
    return;
  }

  // Check if script already exists in DOM
  const existingScript = document.getElementById(SCRIPT_ID);
  if (existingScript) {
    // Script exists, wait for it to load
    if (isGoogleMapsReady()) {
      isLoaded = true;
      isLoading = false;
      loadCallbacks.forEach(cb => cb(null));
      loadCallbacks = [];
    } else {
      // Wait for it to be ready
      const checkReady = setInterval(() => {
        if (isGoogleMapsReady()) {
          clearInterval(checkReady);
          isLoaded = true;
          isLoading = false;
          loadCallbacks.forEach(cb => cb(null));
          loadCallbacks = [];
        }
      }, 100);
    }
    return;
  }

  // Start loading
  isLoading = true;

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  // Remove loading=async to ensure synchronous loading
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
  script.async = true;
  script.defer = true;

  script.onload = () => {
    // Wait a bit for the API to fully initialize
    const checkReady = setInterval(() => {
      if (isGoogleMapsReady()) {
        clearInterval(checkReady);
        isLoaded = true;
        isLoading = false;
        loadCallbacks.forEach(cb => cb(null));
        loadCallbacks = [];
      }
    }, 50);
    
    // Timeout after 5 seconds
    setTimeout(() => {
      clearInterval(checkReady);
      if (!isLoaded) {
        isLoading = false;
        loadCallbacks.forEach(cb => cb(new Error('Google Maps failed to initialize')));
        loadCallbacks = [];
      }
    }, 5000);
  };

  script.onerror = (error) => {
    isLoading = false;
    loadCallbacks.forEach(cb => cb(error));
    loadCallbacks = [];
  };

  document.head.appendChild(script);
};

// Custom hook for components that need Google Maps
export function useGoogleMaps() {
  const [loaded, setLoaded] = useState(isGoogleMapsReady());
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'your_google_maps_api_key_here') {
      setError('Google Maps API key not configured');
      return;
    }

    // If already loaded
    if (isGoogleMapsReady()) {
      setLoaded(true);
      return;
    }

    loadGoogleMapsScript((err) => {
      if (err) {
        setError('Failed to load Google Maps');
        console.error('Google Maps load error:', err);
      } else {
        setLoaded(true);
      }
    });
  }, []);

  return { loaded, error, google: window.google };
}

// Check if Google Maps Places API is available
export function useGooglePlaces() {
  const { loaded, error, google } = useGoogleMaps();
  const [placesReady, setPlacesReady] = useState(false);

  useEffect(() => {
    if (loaded && google?.maps?.places) {
      setPlacesReady(true);
    }
  }, [loaded, google]);

  return { 
    loaded: placesReady, 
    error,
    AutocompleteService: google?.maps?.places?.AutocompleteService,
    AutocompleteSessionToken: google?.maps?.places?.AutocompleteSessionToken,
    PlacesService: google?.maps?.places?.PlacesService,
    Place: google?.maps?.places?.Place,
    AutocompleteSuggestion: google?.maps?.places?.AutocompleteSuggestion,
  };
}

export default useGoogleMaps;
